import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * A link to one record of a list page, e.g. a notification's (notificationTarget): `?record=<ItemId>`, and
 * `?tab=<value>` when the list is on a tab other than the page's first.
 */
export const LINKED_RECORD_PARAM = 'record';
export const LINKED_TAB_PARAM = 'tab';

/** The page's path with a link to one of its records; the bare path without one. */
export const linkToRecord = (path: string, itemId?: string, tab?: string): string => {
  if (!itemId) return path;
  // `path` may carry a query of its own (e.g. `/service-requests?type=noc`).
  const [pathname, query = ''] = path.split('?');
  const params = new URLSearchParams({ [LINKED_RECORD_PARAM]: itemId });
  if (tab) params.set(LINKED_TAB_PARAM, tab);
  new URLSearchParams(query).forEach((value, key) => params.set(key, value));
  return `${pathname}?${params.toString()}`;
};

/**
 * The record the page was linked to (`linkedId`, '' for none) and `clearLinked` to show the whole list
 * again. Hand `linkedId` to `useServerTable`, which then lists only that record.
 */
export const useLinkedRecord = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedId = searchParams.get(LINKED_RECORD_PARAM) ?? '';
  const linkedTab = searchParams.get(LINKED_TAB_PARAM) ?? '';

  const clearLinked = useCallback(
    () =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(LINKED_RECORD_PARAM);
          next.delete(LINKED_TAB_PARAM);
          return next;
        },
        { replace: true }
      ),
    [setSearchParams]
  );

  return { linkedId, linkedTab, clearLinked };
};

/** Opens (`open`) the linked record once it is among the listed `items`; once per link. */
export const useOpenLinkedRecord = <TItem extends { ItemId: string }>(
  linkedId: string,
  items: TItem[],
  open: (item: TItem) => void
) => {
  const openedRef = useRef('');
  const item = linkedId ? items.find((row) => row.ItemId === linkedId) : undefined;

  useEffect(() => {
    if (!linkedId) openedRef.current = '';
    if (!item || openedRef.current === linkedId) return;
    openedRef.current = linkedId;
    open(item);
  }, [linkedId, item, open]);
};

/**
 * A page's tab kept in the URL (`?tab=`), so a link, a reload and Back open the same tab. `tabs` are the
 * tabs the user may see; any other `?tab` (or none) opens `defaultTab`. `aliases` map old tab names
 * that links already sent still hold. Switching tabs replaces the URL (no new history entry) and drops
 * a linked `?record=`, which belongs to the tab it was sent for.
 */
export const useUrlTab = <TTab extends string>(
  tabs: readonly TTab[],
  defaultTab: TTab,
  aliases: Readonly<Record<string, TTab>> = {},
  /** Params that belong to one tab (e.g. `archived`), cleared when the tab changes. */
  reset: readonly string[] = []
) => useUrlChoice(LINKED_TAB_PARAM, tabs, defaultTab, { aliases, reset });

/**
 * One of `choices` kept in the URL as `?<param>=` (a tab, a view, a filter switch), like `useUrlTab`:
 * an unknown or missing value is `defaultChoice`, and choosing replaces the URL and drops `?record=`
 * and the `reset` params. Choosing `defaultChoice` removes the param (a tab is always written).
 */
export function useUrlChoice<TChoice extends string>(
  param: string,
  choices: readonly TChoice[],
  defaultChoice: TChoice,
  {
    aliases = {},
    reset = [],
  }: { aliases?: Readonly<Record<string, TChoice>>; reset?: readonly string[] } = {}
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get(param) ?? '';
  const named = (aliases[requested] ?? requested) as TChoice;
  const choice = choices.includes(named) ? named : defaultChoice;
  const resetKey = reset.join(',');

  const setChoice = useCallback(
    (next: string) =>
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === defaultChoice && param !== LINKED_TAB_PARAM) params.delete(param);
          else params.set(param, next);
          params.delete(LINKED_RECORD_PARAM);
          resetKey.split(',').forEach((key) => key && params.delete(key));
          return params;
        },
        { replace: true }
      ),
    [setSearchParams, param, defaultChoice, resetKey]
  );

  return [choice, setChoice] as const;
}

/** A page's tab, switched to the linked one (`?tab=`) whenever a link names one. */
export const useLinkedTab = (defaultTab: string) => {
  const { linkedTab } = useLinkedRecord();
  const [tab, setTab] = useState(linkedTab || defaultTab);

  useEffect(() => {
    if (linkedTab) setTab(linkedTab);
  }, [linkedTab]);

  return [tab, setTab] as const;
};
